/**
 * Named guards for values that reach us as one of several representations: a
 * route query that is a string or an array of them, a money field the API sends
 * as a decimal string, a form input that is empty rather than absent.
 *
 * A type predicate is where a `typeof` check belongs. The narrowing gets a name
 * and a contract, and the call site reads as the question it is actually asking
 * rather than as the shape it happens to be checking.
 */

/** Whether the value is a string, whatever else it could have been. */
export function isString<T>(value: T | string): value is string {
    return typeof value === 'string';
}

/** Whether the value is a string carrying something — blank counts as absent. */
export function isFilledString<T>(value: T | string): value is string {
    return typeof value === 'string' && value.trim() !== '';
}

/** Whether the value is already a number, rather than a string standing in for one. */
export function isNumber<T>(value: T | number): value is number {
    return typeof value === 'number';
}

/** Whether the value is a boolean, which URL parameters and form state both blur. */
export function isBoolean<T>(value: T | boolean): value is boolean {
    return typeof value === 'boolean';
}

/** Whether the value is a function rather than the value it would have returned. */
export function isFunction<T, R>(value: T | (() => R)): value is () => R {
    return typeof value === 'function';
}
